import { Space, Typography } from "antd";
import { apiHost } from "consts";
import bem from "easy-bem";
import CardSample from "features/cases/blocks/CommonCard";
import { useIsMobile } from "hooks/useIsMobile";
import { useTexts } from "hooks/useTexts";
import { useUser } from "hooks/useUser";
import React, { useCallback, useState } from "react";
import ShareLink from "ui/ShareLink";

import type { AttributeSectionProps } from "./AttributeSection.types";
import { blendWithWhite, withAnchor } from "./AttributeSection.utils";
import AttributeDescriptionModal from "./elements/AttributeDescriptionModal";
import { useMobileScrollIntoView } from "./hooks/useMobileScrollIntoView";
import "./AttributeSection.css";

const cn = bem("attr-section");

const AttributeSection: React.FC<AttributeSectionProps> = ({
  attr,
  cards,
  variants,
  attributeOf,
  problemId,
  solutionId,
  disabled = false,
  dateDisplayMode,
}) => {
  const { isMobile } = useIsMobile();
  const { txt } = useTexts("case");
  const { isContributor, isBannedFromPublishing } = useUser();

  const canAddCard = isContributor && !isBannedFromPublishing;

  const attrColor = "#" + attr.color;
  const attrLightColor = blendWithWhite("#" + attr.lightColor);

  const { ref: refContainer, onClick: clickScroll } = useMobileScrollIntoView();

  const hasDescriptionModal = !!attr.description;

  const [cardsVisible, setCardsVisible] = useState(
    cards.length || (isContributor ? attr?.isPrimary : true),
  );

  const toggleSection = useCallback(() => {
    if (disabled) return;
    setCardsVisible(!cardsVisible);
  }, [cardsVisible, disabled]);

  const attrUrlId = attr.slug ? attr.slug : `attribute-${attr.id}`;
  const anchorUrl = withAnchor(window.location.href, attrUrlId);

  return (
    <div
      onClick={clickScroll}
      ref={refContainer}
      key={attr.id}
      id={attrUrlId}
      className={cn({ mobile: isMobile, disabled, collapsed: !cardsVisible })}
      style={{ background: "#" + attr.lightColor }}
    >
      <Typography.Title
        level={4}
        onClick={toggleSection}
        className={cn("title", { "title--clickable": hasDescriptionModal })}
      >
        {attr?.title}{" "}
        <ShareLink
          url={anchorUrl}
          elementName={
            txt("attribute_block", "блок атрибутов") + ` «${attr?.title}»`
          }
        />
        <AttributeDescriptionModal
          title={attr.title}
          content={attr.description}
        />
      </Typography.Title>

      {cardsVisible && (
        <Space
          direction="horizontal"
          align="start"
          size="middle"
          wrap={!isMobile}
          style={{ display: "flex" }}
          className={cn("cards", { mobile: isMobile })}
        >
          {canAddCard && (
            <CardSample
              parentData={{
                isAttribute: true,
                attributeType: String(attr.id),
                problemId: problemId,
                solutionId: solutionId,
                attributeOf: attributeOf,
                attr: attr,
              }}
              objectColor={attrLightColor}
              highlightColor={attrColor}
              key={"new"}
              addUrl={`${apiHost}/admin/solutions/attributecard/add/?author=1&attributeOf=${attributeOf}&attributeType=${attr.id}&problem=${problemId}&solution=${solutionId}`}
            />
          )}

          {cards.map((card) => {
            const cardVariant = variants?.find(
              (variant: { id: any }) => variant.id === card.attributeVariant,
            );

            return (
              <CardSample
                meta={card.meta}
                cardContentId={card.id}
                parentData={{
                  isAttribute: true,
                  attributeOf: attributeOf,
                  attributeType: String(attr.id),
                  attributeVariant: cardVariant,
                  problemId: problemId,
                  solutionId: solutionId,
                  attr,
                }}
                sectionTitle={attr.title}
                objectColor={attrLightColor}
                highlightColor={attrColor}
                key={card.id}
                title={card?.title}
                description={card?.description}
                dateDisplayMode={dateDisplayMode}
                editUrl={`${apiHost}/admin/solutions/attributecard/${card?.id}/change/`}
                addUrl={`${apiHost}/admin/solutions/attributecard/add/?author=1&attributeOf=${attributeOf}&attributeType=${attr.id}&problem=${problemId}&solution=${solutionId}`}
              />
            );
          })}
        </Space>
      )}
    </div>
  );
};

export default AttributeSection;
